import { Box, Tooltip, Typography } from "@braintrust/braintrust-ui-components";
import { InfoIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useUserNeedsIdentityVerification } from "@js/apps/identity-verification";

import {
  getApplicationStatusColorVariant,
  getApplicationTooltipText,
} from "./utils";

import styles from "./style.module.scss";

export type ApplicationStatusWithTooltipProps =
  Partial<ApplictionStatusProps> & {
    appliedDate?: string | null;
    offerCreatedDate?: string | null;
    offerAcceptedDate?: string | null;
    offerUpdatedDate: string | null | undefined;
    hideIcon?: boolean;
  };

export const ApplicationStatusWithTooltip = ({
  status,
  appliedDate,
  offerCreatedDate,
  offerAcceptedDate,
  offerUpdatedDate,
  hideIcon,
}: ApplicationStatusWithTooltipProps) => {
  const { userFailedIdentityVerification } = useUserNeedsIdentityVerification();
  if (!status || noPillForStatus(status)) return null;

  const title = getApplicationTooltipText({
    status,
    appliedDate,
    offerCreatedDate,
    offerAcceptedDate,
    offerUpdatedDate,
    userFailedIdentityVerification,
  });

  return (
    <Tooltip
      title={title}
      color="var(--black)"
      placement="top"
      maxWidth={300}
      disabled={!title}
    >
      <span className={styles.tooltip} style={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <ApplictionStatus
            status={status}
            userFailedIdentityVerification={userFailedIdentityVerification}
          />
          {title && !hideIcon && <InfoIcon sx={{ fontSize: "20px" }} />}
        </Box>
      </span>
    </Tooltip>
  );
};

const OFFER_REQUIRES_ACCEPTANCE_STATUSES: Array<
  EnumType<typeof ENUMS.JobFreelancerStatus>
> = [
  ENUMS.JobFreelancerStatus.NEW_OFFER,
  ENUMS.JobFreelancerStatus.UPDATED_OFFER,
  ENUMS.JobFreelancerStatus.OFFER_EXTENDED,
];

type ApplictionStatusProps = {
  status: EnumType<typeof ENUMS.JobFreelancerStatus>;
  userFailedIdentityVerification?: boolean;
};
export const ApplictionStatus = ({
  status,
  userFailedIdentityVerification,
}: ApplictionStatusProps) => {
  if (noPillForStatus(status)) return null;

  if (
    OFFER_REQUIRES_ACCEPTANCE_STATUSES.includes(status) &&
    userFailedIdentityVerification
  ) {
    return (
      <Typography
        className={styles.applicationStatusPill}
        component="p"
        size="small"
        title="Can’t accept offer"
        ellipsis
        sx={{
          backgroundColor: "var(--medium-red)",
          color: "var(--dark-red)",
        }}
      >
        Can’t accept offer
      </Typography>
    );
  }

  const variant = getApplicationStatusColorVariant(status);

  return (
    <Typography
      className={styles.applicationStatusPill}
      component="p"
      size="small"
      title={ENUMS.JobFreelancerStatusLabels[status]}
      ellipsis
      sx={{
        backgroundColor: variant.bgColor,
        color: variant.textColor,
      }}
    >
      {ENUMS.JobFreelancerStatusLabels[status]}
    </Typography>
  );
};

const NO_PILL_STATUSES: Array<EnumType<typeof ENUMS.JobFreelancerStatus>> = [
  ENUMS.JobFreelancerStatus.ACCEPTING_APPLICATIONS,
  ENUMS.JobFreelancerStatus.APPLICATION_SENT,
];

const noPillForStatus = (
  status?: EnumType<typeof ENUMS.JobFreelancerStatus>,
): boolean => {
  return !status || NO_PILL_STATUSES.includes(status);
};
